import React, { Fragment } from 'react';
import { render } from 'react-dom';
import './index.css';
import './App.Styles.ts';
import 'fontsource-roboto';
import { BrowserRouter as ReactRouter } from 'react-router-dom';
import './App.Styles.ts';
import theme from './App.Styles';
import { Layout } from './components/generic-components/layout/Layout.Component';
import { ThemeProvider } from '@material-ui/core';
import Router from './support/config/Router';
import { ToastContainer } from 'react-toastify';
import { AuthenticatedTemplate, useMsalAuthentication, useMsal, UnauthenticatedTemplate } from '@azure/msal-react';
import 'react-toastify/dist/ReactToastify.css';
import { InteractionType, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { AuthenticationVerifier } from './components/generic-components/authentication-verifier/AuthenticationVerifier.Component';
import { TenantPortalAuthBoundary } from './components/generic-components/tenant-portal-auth-boundary/TenantPortalAuthBoundary.Component';
import {
    resetPasswordUri,
    loginUri,
    scopes,
    isTenantPortalEntry,
    TENANT_PORTAL_EXPLICIT_LOGOUT_KEY,
} from './b2cConfiguration';
import { Clarity } from './components/generic-components/clarity/Clarity';

// Evaluated once at module load: is this a tenant portal deeplink (e.g. /claims/{id})?
const tenantPortalEntry = isTenantPortalEntry();

if (!tenantPortalEntry) {
    sessionStorage.removeItem(TENANT_PORTAL_EXPLICIT_LOGOUT_KEY);
}

/**
 * Shared B2C session guard used by both auth paths:
 * - Redirect to the correct B2C policy on password-reset / cancel error codes.
 * - Log out when the host changes (Dev <-> Test <-> Prod) since the same B2C
 *   configuration is reused across environments.
 */
const useB2cSessionGuard = (instance: IPublicClientApplication) => {
    React.useEffect(() => {
        const callbackId = instance.addEventCallback((message) => {
            const { error } = message;
            if (error !== null && error.message.includes('AADB2C90118')) {
                instance.loginRedirect({
                    authority: resetPasswordUri,
                    scopes: scopes
                });
                return;
            }
            if (error !== null && error.message.includes('AADB2C90091')) {
                instance.loginRedirect({
                    authority: loginUri,
                    scopes: scopes
                });
                return;
            }
        });

        // Detect environment changes (Dev <-> Test <-> Producción) and close session.
        const currentHost = globalThis.location.hostname;
        const lastHost = localStorage.getItem('lastHost');

        if (lastHost && lastHost !== currentHost) {
            instance.logoutRedirect({ postLogoutRedirectUri: globalThis.location.origin });
        }

        localStorage.setItem('lastHost', currentHost);

        return () => callbackId && instance.removeEventCallback(callbackId);
    }, [instance]);
};

// Shared authenticated app tree, reused by both entry paths.
const AuthenticatedApp = () => {
    const { instance, accounts } = useMsal();

    // Environment-based Clarity configuration
    // Store as strings to prevent Webpack from tree-shaking the ADO tokens
    const clarityEnabled = String(process.env.REACT_APP_CLARITY_ENABLED);
    const clarityProjectId = String(process.env.REACT_APP_CLARITY_PROJECT_ID);

    return (
        <>
        {clarityEnabled === 'true' && clarityProjectId && (
            <Clarity clarityId={clarityProjectId} />
        )}

        <Fragment>
            <AuthenticatedTemplate>
                {accounts ? (
                    <AuthenticationVerifier accounts={accounts} instance={instance}>
                        <ReactRouter>
                            <ThemeProvider theme={theme}>
                                <Layout>
                                    <Router></Router>
                                    <div id="router-footer" />
                                    <ToastContainer autoClose={3000} hideProgressBar></ToastContainer>
                                </Layout>
                            </ThemeProvider>
                        </ReactRouter>
                    </AuthenticationVerifier>
                ) : (
                    ''
                )}
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate></UnauthenticatedTemplate>
        </Fragment>
        </>
    );
};

// Direct access (vendor users): existing redirect-based MSAL auth.
const DirectAccessApp = () => {
    const loginRequest: RedirectRequest = { scopes: scopes };
    useMsalAuthentication(InteractionType.Redirect, loginRequest);
    const { instance } = useMsal();

    useB2cSessionGuard(instance);

    return <AuthenticatedApp />;
};

// Tenant portal deeplink: federate to Azure AD via domain_hint=commonaad.
const TenantPortalApp = () => {
    const { instance } = useMsal();

    useB2cSessionGuard(instance);

    return (
        <TenantPortalAuthBoundary instance={instance}>
            <AuthenticatedApp />
        </TenantPortalAuthBoundary>
    );
};

const App = () => (tenantPortalEntry ? <TenantPortalApp /> : <DirectAccessApp />);

render(<App />, document.getElementById('root'));

export default App;
